import React, { useCallback, useMemo, useState } from 'react';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../../common/types';
import { ReleaseTab, ReleaseTabProps } from './releaseTabProps';
import { ThemePanel } from './theme/ThemePanel';
import { ReleaseThemeDialog } from './theme/ReleaseThemeDialog';
import { OverviewTab } from './overview/OverviewTab';
import { KitTab } from './kit/KitTab';
import { PostsBoard } from '../posts/PostsBoard';
import { ReleasePostsCoverRedo } from './kit/coverRedo/ReleasePostsCoverRedo';
import { ReleasePostsDateRedo } from './kit/dateRedo/ReleasePostsDateRedo';
import { PlanTab } from './plan/PlanTab';
import { CampaignTab } from './campaign/CampaignTab';
import { ReleaseWorkingBanner } from './ReleaseWorkingBanner';
import { ReleaseUnlockCards } from './unlock/ReleaseUnlockCards';
import { FirstImagesHeader } from './unlock/FirstImagesHeader';
import { useShowUnlockCards } from './unlock/useShowUnlockCards';
import { countWorkingImages } from './unlock/countWorkingImages';
import { useQueryParam } from '../../../../utils/hooks/useQueryParam';
import { RELEASE_THEME_EDIT } from './releasePageLink';

interface ReleaseTabBodyProps extends ReleaseTabProps {
  tab: ReleaseTab;
  onThemeDirtyChange: (isDirty: boolean) => void;
  onChangeTab: (tab: ReleaseTab) => void;
}

/**
 * The body of the open tab. The Theme panel sits on Overview as a summary (design b1; any member
 * who can make assets changes the theme, owner answer 5). "Edit theme" opens the editor in a dialog
 * (owner, 2026-10-07): the Overview column is too narrow for it. "Show them" of its stale note
 * opens the Kit tab. The working banner shows on Overview and Kit (design e4), after the unlock
 * cards of the first release (design: onboarding c5). While the first images of the first release
 * render, the Kit tab shows the head "Your first images" in place of the banner (c5). The Posts tab
 * is the Posts board of the song (design assets-rethink c1). The Auto campaign tab is the AI reels
 * campaign of the song (`campaign/CampaignTab`).
 */
const ReleaseTabBodyComponent: React.FC<ReleaseTabBodyProps> = (props) => {
  const { tab, accountId, item, release, onThemeDirtyChange, onChangeTab } = props;
  // `theme=edit` (the checklist step "Pick a look") opens the theme editor at once.
  const themeParam = useQueryParam('theme', undefined, '');
  const [isThemeOpen, setIsThemeOpen] = useState(themeParam === RELEASE_THEME_EDIT);
  const openTheme = useCallback(() => setIsThemeOpen(true), []);
  const closeTheme = useCallback(() => setIsThemeOpen(false), []);
  const showStale = useCallback(() => {
    setIsThemeOpen(false);
    onChangeTab('kit');
  }, [onChangeTab]);

  const isFirstRelease = useShowUnlockCards(item.uuid);
  const firstImages = useMemo(
    () => (isFirstRelease ? countWorkingImages(release.assets) : 0),
    [isFirstRelease, release.assets]
  );

  if (tab === 'plan') {
    return <PlanTab accountId={accountId} item={item} release={release} />;
  }
  if (tab === 'campaign') {
    return <CampaignTab accountId={accountId} item={item} release={release} />;
  }
  if (tab === 'posts') {
    // design: assets-rethink c1. The Posts board of this one song.
    return (
      <>
        {/* design: kit-redo-cover a — the Posts tab shows the same banner (AI reels and post files are here). */}
        <ReleasePostsCoverRedo accountId={accountId} catalogItemUuid={item.uuid} />
        <ReleasePostsDateRedo accountId={accountId} catalogItemUuid={item.uuid} />
        <PostsBoard accountId={accountId} catalogItemUuid={item.uuid} />
      </>
    );
  }
  // The rule of `presaveNeedsDate` (`onboarding.setFirstRelease`): a song that is not out, with no
  // release day, has no pre-save page. The unlock cards then say how to get one.
  const needsReleaseDay =
    item.kind === CatalogItemKind.unreleased && item.releaseDate === null && item.presaveCampaignUuid === null;
  // design: onboarding c5. The unlock cards lead Overview and Assets of the first release.
  const unlockCards = (
    <ReleaseUnlockCards accountId={accountId} catalogItemUuid={item.uuid} needsReleaseDay={needsReleaseDay} />
  );
  const banner = (
    <>
      {unlockCards}
      <ReleaseWorkingBanner item={item} release={release} />
    </>
  );
  if (tab === 'kit') {
    return (
      <>
        {firstImages > 0 ? (
          <>
            {unlockCards}
            <FirstImagesHeader count={firstImages} />
          </>
        ) : (
          banner
        )}
        <KitTab accountId={accountId} item={item} release={release} />
      </>
    );
  }
  return (
    <>
      {banner}
      <OverviewTab
        accountId={accountId}
        item={item}
        release={release}
        onOpenTab={onChangeTab}
        themePanel={
          <ThemePanel
            key={item.uuid}
            accountId={accountId}
            item={item}
            kit={release.kit}
            assets={release.assets}
            onShowStale={showStale}
            isCollapsed
            onExpand={openTheme}
            onCollapse={closeTheme}
          />
        }
      />
      {isThemeOpen && (
        <ReleaseThemeDialog
          key={item.uuid}
          isOpen
          accountId={accountId}
          item={item}
          kit={release.kit}
          assets={release.assets}
          onClose={closeTheme}
          onDirtyChange={onThemeDirtyChange}
        />
      )}
    </>
  );
};

export const ReleaseTabBody = TypedMemo(ReleaseTabBodyComponent);
